import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  CategoryMap,
  RetardantFuelCategory,
  RetardantProductInput,
  RetardantProductOption,
  useCreateRetardantProduct,
} from '../api/retardantProducts';

/**
 * "Add retardant product" — opened from the bottom of the product picker in
 * the treatment modal. Asks for what a datasheet or fire test report states
 * (percent reductions, "times slower to ignite") and converts to the model's
 * treated÷untreated ratios on save. The product belongs to the user's
 * organisation and appears in its picker straight away.
 */

const CATEGORY_LABELS: Record<RetardantFuelCategory, { label: string; hint: string }> = {
  fine_flashy: { label: 'Grass & shrub', hint: 'grass, heath, shrub — fine fuels that ignite fast' },
  needle_litter: { label: 'Forest litter', hint: 'pine needles, leaf litter, forest floor' },
  coarse_woody: { label: 'Heavy fuels', hint: 'slash, logs, heavy woody debris' },
};
const CATEGORIES = Object.keys(CATEGORY_LABELS) as RetardantFuelCategory[];

const COLUMNS: { key: 'heat' | 'energy' | 'ignition'; title: string; help: string }[] = [
  {
    key: 'heat',
    title: 'Reduction in peak heat release',
    help: 'How much less intensely the treated fuel burns at its peak. A datasheet or test report gives this as "peak heat release rate reduced by N%". Drives how fast and how hot the fire spreads.',
  },
  {
    key: 'energy',
    title: 'Reduction in total heat released',
    help: 'How much less total energy the treated fuel gives off before it is spent. Reported as "total heat released reduced by N%". Drives fireline intensity and flame height.',
  },
  {
    key: 'ignition',
    title: 'Ignition delay',
    help: 'How many times longer the treated fuel takes to ignite. If the report gives times, divide treated by untreated (46 s ÷ 11 s = 4.2). Tick "Does not ignite" instead if the fuel never caught in the test.',
  },
];

type RowDraft = { heat: string; energy: string; ignition: string; noIgnite: boolean };
type Draft = {
  name: string;
  description: string;
  uniform: boolean;
  rows: Record<RetardantFuelCategory, RowDraft>;
  half_life_days: string;
  residual_efficacy_floor: string;
  persistence_horizon_days: string;
};

const emptyDraft = (): Draft => ({
  name: '',
  description: '',
  uniform: true,
  rows: Object.fromEntries(
    CATEGORIES.map((c) => [c, { heat: '', energy: '', ignition: '', noIgnite: false }])
  ) as Draft['rows'],
  half_life_days: '180',
  residual_efficacy_floor: '0',
  persistence_horizon_days: '730',
});

const fromDraft = (d: Draft): RetardantProductInput => {
  const ce: CategoryMap = {};
  const af: CategoryMap = {};
  const delay: CategoryMap = {};
  const suppress: RetardantFuelCategory[] = [];
  for (const c of CATEGORIES) {
    const row = d.uniform ? d.rows[CATEGORIES[0]] : d.rows[c];
    if (row.heat !== '') ce[c] = 1 - Number(row.heat) / 100;
    if (row.energy !== '') af[c] = 1 - Number(row.energy) / 100;
    if (row.noIgnite) suppress.push(c);
    else if (row.ignition !== '') delay[c] = Number(row.ignition);
  }
  return {
    name: d.name.trim(),
    description: d.description,
    combustion_efficiency: ce,
    available_fuel: af,
    ignition_delay: delay,
    suppresses_ignition: suppress,
    half_life_days: Number(d.half_life_days),
    residual_efficacy_floor: Number(d.residual_efficacy_floor) / 100,
    persistence_horizon_days: Number(d.persistence_horizon_days),
  };
};

const draftProblems = (d: Draft): string[] => {
  const problems: string[] = [];
  if (!d.name.trim()) problems.push('Give the product a name.');
  const rows = d.uniform ? [d.rows[CATEGORIES[0]]] : CATEGORIES.map((c) => d.rows[c]);
  for (const r of rows) {
    for (const f of ['heat', 'energy'] as const) {
      if (r[f] !== '' && (Number.isNaN(Number(r[f])) || Number(r[f]) < 0 || Number(r[f]) > 100))
        problems.push('Reductions must be between 0% and 100%.');
    }
    if (!r.noIgnite && r.ignition !== '' && (Number.isNaN(Number(r.ignition)) || Number(r.ignition) < 1))
      problems.push('Ignition delay must be 1 or more (1 = no delay).');
  }
  if (rows.every((r) => r.heat === '' && r.energy === '' && r.ignition === '' && !r.noIgnite))
    problems.push('Enter at least one figure — as it stands this product would have no effect.');
  for (const f of ['half_life_days', 'persistence_horizon_days'] as const) {
    if (Number.isNaN(Number(d[f])) || Number(d[f]) <= 0) problems.push('Duration values must be positive numbers of days.');
  }
  const floor = Number(d.residual_efficacy_floor);
  if (Number.isNaN(floor) || floor < 0 || floor > 100) problems.push('Residual effectiveness must be between 0% and 100%.');
  return Array.from(new Set(problems));
};

const Help = ({ text }: { text: string }) => (
  <Tooltip title={text} placement="top" arrow>
    <InfoOutlinedIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
  </Tooltip>
);

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the new product, which the picker then selects. */
  onCreated: (product: RetardantProductOption) => void;
}

export default function AddRetardantProductDialog({ open, onClose, onCreated }: Props) {
  const create = useCreateRetardantProduct();
  const [d, setD] = useState<Draft>(emptyDraft);
  const [showProblems, setShowProblems] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const patch = (p: Partial<Draft>) => setD((cur) => ({ ...cur, ...p }));
  const setRow = (c: RetardantFuelCategory, p: Partial<RowDraft>) =>
    setD((cur) => ({ ...cur, rows: { ...cur.rows, [c]: { ...cur.rows[c], ...p } } }));

  const problems = draftProblems(d);
  const visibleRows: RetardantFuelCategory[] = d.uniform ? [CATEGORIES[0]] : CATEGORIES;

  const close = () => {
    setD(emptyDraft());
    setShowProblems(false);
    setServerError(null);
    onClose();
  };

  const save = () => {
    if (problems.length) {
      setShowProblems(true);
      return;
    }
    create.mutate(fromDraft(d), {
      onSuccess: (product) => {
        onCreated(product);
        close();
      },
      onError: (err) =>
        setServerError((err as { message?: string })?.message || 'Could not save the product'),
    });
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Add retardant product</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '8px !important' }}>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        {showProblems && problems.length > 0 && (
          <Alert severity="warning">
            {problems.map((m) => (
              <div key={m}>{m}</div>
            ))}
          </Alert>
        )}

        <TextField label="Product name" value={d.name} onChange={(e) => patch({ name: e.target.value })} fullWidth autoFocus />
        <TextField
          label="Description"
          value={d.description}
          onChange={(e) => patch({ description: e.target.value })}
          multiline
          minRows={2}
          helperText="Optional — shown when choosing this product."
        />

        <Box>
          <Typography variant="subtitle2">How effective is it?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Enter the figures from the product's datasheet or fire test report. Leave a figure
            blank if the product has no rating for it — it is then modelled as having no effect
            there.
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={d.uniform} onChange={(e) => patch({ uniform: e.target.checked })} />}
            label={
              <Typography variant="body2">
                Same figures for every fuel type
                <Help text="Most datasheets give one set of figures. Turn this off if the test report rates grass, forest litter and heavy fuels separately." />
              </Typography>
            }
          />
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 170 }}>{d.uniform ? '' : 'Fuel type'}</TableCell>
                {COLUMNS.map((col) => (
                  <TableCell key={col.key}>
                    {col.title}
                    <Help text={col.help} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((c) => {
                const row = d.rows[c];
                return (
                  <TableRow key={c}>
                    <TableCell>
                      {d.uniform ? (
                        <Typography variant="body2" color="text.secondary">All fuel types</Typography>
                      ) : (
                        <>
                          {CATEGORY_LABELS[c].label}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {CATEGORY_LABELS[c].hint}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    {(['heat', 'energy'] as const).map((f) => (
                      <TableCell key={f}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="e.g. 50"
                          value={row[f]}
                          onChange={(e) => setRow(c, { [f]: e.target.value })}
                          slotProps={{
                            input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                            htmlInput: { min: 0, max: 100, step: 1 },
                          }}
                          sx={{ width: 130 }}
                        />
                      </TableCell>
                    ))}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="e.g. 4"
                          value={row.noIgnite ? '' : row.ignition}
                          disabled={row.noIgnite}
                          onChange={(e) => setRow(c, { ignition: e.target.value })}
                          slotProps={{
                            input: { endAdornment: <InputAdornment position="end">× slower</InputAdornment> },
                            htmlInput: { min: 1, step: 0.1 },
                          }}
                          sx={{ width: 150 }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox size="small" checked={row.noIgnite} onChange={(e) => setRow(c, { noIgnite: e.target.checked })} />
                          }
                          label={<Typography variant="body2">Does not ignite</Typography>}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Box>
          <Typography variant="subtitle2">How long does it last?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            Effectiveness fades from the day of application. The defaults suit a season-long
            product; change them if the manufacturer states a duration.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Half-life"
              type="number"
              value={d.half_life_days}
              onChange={(e) => patch({ half_life_days: e.target.value })}
              helperText="Effectiveness halves every this many days"
              slotProps={{ input: { endAdornment: <InputAdornment position="end">days</InputAdornment> } }}
              sx={{ width: 200 }}
            />
            <TextField
              label="Fully worn off after"
              type="number"
              value={d.persistence_horizon_days}
              onChange={(e) => patch({ persistence_horizon_days: e.target.value })}
              helperText="No effect at all beyond this"
              slotProps={{ input: { endAdornment: <InputAdornment position="end">days</InputAdornment> } }}
              sx={{ width: 200 }}
            />
            <TextField
              label="Residual effectiveness"
              type="number"
              value={d.residual_efficacy_floor}
              onChange={(e) => patch({ residual_efficacy_floor: e.target.value })}
              helperText="Kept until fully worn off (usually 0)"
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                htmlInput: { min: 0, max: 100, step: 5 },
              }}
              sx={{ width: 200 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={create.isPending}>
          Add product
        </Button>
      </DialogActions>
    </Dialog>
  );
}
